import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import toast from "react-hot-toast";
import { createCashMemoAPI, getAllCashMemosAPI, updateCashMemoAPI } from "./cashMemoAPI";

// Temporary definitions for types until proper global types are resolved
export interface RefillEntry {
    memoNumber: string;
    agentName: string;
    totalRefillDelivered: number;
    cdcmsCashMemoPunch: number;
    vitaranCashMemoPunch: number;
    totalCashMemoPunch: number;
    remainingCashMemo: number;
    remarks: string;
}

export interface ICashMemoData extends RefillEntry {
    _id?: string;
    date?: string;
    createdAt?: string;
    updatedAt?: string;
    __v?: number;
}

export interface IStandardAPIResponse<T> {
    success: boolean;
    message?: string;
    data?: T;
    status?: string;
}

export interface ICashMemoResponse extends IStandardAPIResponse<ICashMemoData | ICashMemoData[] | null> { }


export interface CashMemoState {
    cashMemos: ICashMemoData[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: CashMemoState = {
    cashMemos: [],
    status: "idle",
    error: null,
};

export const createCashMemoAsync = createAsyncThunk(
    "cashMemo/create",
    async (cashMemoData: RefillEntry, { rejectWithValue }) => {
        try {
            const result: any = await createCashMemoAPI(cashMemoData);
            if (result.data) {
                return result.data;
            }

            return result.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred while creating cash memo.");
        }
    }
);

export const fetchCashMemosAsync = createAsyncThunk(
    "cashMemo/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const result: ICashMemoResponse = await getAllCashMemosAPI();
            if (result.success && Array.isArray(result.data)) {
                return result.data;
            } else {
                return rejectWithValue(result.message || "Failed to fetch cash memos.");
            }
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred while fetching cash memos.");
        }
    }
);

export const updateCashMemoAsync = createAsyncThunk(
    "cashMemo/update",
    async ({ id, cashMemoData }: { id: string; cashMemoData: ICashMemoData }, { rejectWithValue }) => {
        try {
            const result: ICashMemoResponse = await updateCashMemoAPI(id, cashMemoData);
            if (result.success) {
                return result.data;
            } else {
                return rejectWithValue(result.message || "Failed to update cash memo.");
            }
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred while updating cash memo.");
        }
    }
);

export const cashMemoSlice = createSlice({
    name: "cashMemo",
    initialState,
    reducers: {
        resetCashMemoStatus: (state) => {
            state.status = "idle";
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createCashMemoAsync.pending, (state) => {
                state.status = "loading";
            })
            .addCase(createCashMemoAsync.fulfilled, (state, action: PayloadAction<ICashMemoData | ICashMemoData[] | null | undefined>) => {
                state.status = "succeeded";
                if (action.payload && !Array.isArray(action.payload)) {
                    state.cashMemos.push(action.payload);
                }
                toast.success("Cash Memo created successfully!");
            })
            .addCase(createCashMemoAsync.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
                toast.error(state.error);
            })
            .addCase(fetchCashMemosAsync.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchCashMemosAsync.fulfilled, (state, action: PayloadAction<ICashMemoData[]>) => {
                state.status = "succeeded";
                state.cashMemos = action.payload;
                toast.success("Cash Memos loaded successfully!");
            })
            .addCase(fetchCashMemosAsync.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
                toast.error(state.error);
            })
            .addCase(updateCashMemoAsync.pending, (state) => {
                state.status = "loading";
            })
            .addCase(updateCashMemoAsync.fulfilled, (state, action: PayloadAction<ICashMemoData | ICashMemoData[] | null | undefined>) => {
                state.status = "succeeded";
                if (action.payload && !Array.isArray(action.payload)) {
                    const index = state.cashMemos.findIndex(memo => memo._id === action.payload?._id);
                    if (index !== -1) {
                        state.cashMemos[index] = action.payload;
                    }
                }
                toast.success("Cash Memo updated successfully!");
            })
            .addCase(updateCashMemoAsync.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
                toast.error(state.error);
            });
    },
});

export const { resetCashMemoStatus } = cashMemoSlice.actions;
export const selectCashMemos = (state: RootState) => state.cashMemo.cashMemos;
export const selectCashMemoStatus = (state: RootState) => state.cashMemo.status;
export const selectCashMemoError = (state: RootState) => state.cashMemo.error;

export default cashMemoSlice.reducer;